import { Controller } from "@hotwired/stimulus"
import { MidiManager } from "../lib/midi"
import type { MidiEvent } from "../lib/types"

// Manages Web MIDI API connection and device selection.
// Dispatches "midi:event" for the game controller to consume.

export default class extends Controller {
  static targets = ["deviceList", "status", "connectBtn"]

  declare deviceListTarget: HTMLSelectElement
  declare statusTarget: HTMLElement
  declare connectBtnTarget: HTMLButtonElement
  declare hasDeviceListTarget: boolean

  private midi = new MidiManager()

  async connect(): Promise<void> {
    const ok = await this.midi.requestAccess()
    if (!ok) {
      this.setStatus("Web MIDI not available")
      return
    }

    this.refreshDevices()
    this.midi.onStateChange(() => this.refreshDevices())
  }

  disconnect(): void {
    this.midi.disconnect()
  }

  refreshDevices(): void {
    if (!this.hasDeviceListTarget) return
    const inputs = this.midi.listInputs()
    this.deviceListTarget.innerHTML = ""

    if (inputs.length === 0) {
      const opt = document.createElement("option")
      opt.textContent = "No MIDI devices found"
      opt.disabled = true
      this.deviceListTarget.appendChild(opt)
      this.setStatus("No devices")
      return
    }

    inputs.forEach((input, i) => {
      const opt = document.createElement("option")
      opt.value = String(i)
      opt.textContent = input.name ?? `Device ${i + 1}`
      this.deviceListTarget.appendChild(opt)
    })
    this.setStatus(`${inputs.length} device(s) found`)
  }

  connectDevice(): void {
    const inputs = this.midi.listInputs()
    const index = parseInt(this.deviceListTarget.value, 10)
    const input = inputs[index]
    if (!input) return

    this.midi.connect(input)
    this.midi.onEvent((evt) => this.handleEvent(evt))
    this.setStatus(`Connected: ${input.name}`)
  }

  disconnectDevice(): void {
    this.midi.disconnect()
    this.setStatus("Disconnected")
  }

  private handleEvent(event: MidiEvent): void {
    this.dispatch("event", { detail: event, bubbles: true })
  }

  private setStatus(text: string): void {
    if (this.statusTarget) {
      this.statusTarget.textContent = text
    }
  }
}
